import { forwardRef, useState, useImperativeHandle } from "react";
import { useForm as useReactForm } from "react-hook-form";
import { BsInfoCircle } from "react-icons/bs";

import { useMutation } from "@tanstack/react-query";

import { OverlayTrigger } from "react-bootstrap";

import { useHistory } from "react-router-dom";

import { format as dateFormat } from "date-fns";
import fileDownload from "js-file-download";
import handleApiError from "../../../../../helpers/handleApiError";

import {
  printManifest,
  simplifiedManifest,
} from "../../../../../services/deliveryService";
import { TooltipStandard } from "../../../../Utility/TooltipStandard";

import LoadingModal from "../../../../LoadingModal";
import DialogModal from "../../../../DialogModal";

import { cn } from "../../../../../helpers/cn";

const printManifestOptions = [
  {
    value: "manifest",
    label: "Manifest",
  },
  {
    value: "simplifiedManifest",
    label: "Simplified Manifest",
  },
];

/**
 * @typedef {Object} submitManifestData
 * @property {string} manifestDatePrint
 * @property {boolean} printAlreadyPrinted
 * @property {string} printManifestType
 */

const onSuccessfulPrintManifest = (response, variables) => {
  const dateParts = variables.manifestDatePrint.split("-");
  const [year, month, day] = dateParts;

  fileDownload(
    response?.data,
    `Diligent Manifest for ${dateFormat(
      new Date(year, month - 1, day),
      "MM-dd-yyyy",
    )}.pdf`,
  );
};

const onErrorPrintManifest = (history) => (error, variables) => {
  if (variables?.printAlreadyPrinted) {
    handleApiError(
      error,
      history,
      true,
      "Error downloading Manifest. You may not have any orders for the selected date.",
    );
  }

  if (!variables?.printAlreadyPrinted) {
    handleApiError(
      error,
      history,
      true,
      "Error downloading Manifest. You may not have any unprinted orders for the selected date.",
    );
  }
};

const PrintManifestModalAdv = forwardRef((_, ref) => {
  const history = useHistory();
  const { mutate: mutatePrintManifest, isLoading: isLoadingPrintManifestData } =
    useMutation({
      mutationFn: ({ manifestDatePrint, printAlreadyPrinted }) =>
        printManifest(manifestDatePrint, printAlreadyPrinted),

      onSuccess: onSuccessfulPrintManifest,

      onError: onErrorPrintManifest(history),
    });

  const {
    mutate: mutateSimplifiedManifest,
    isLoading: isLoadingSimplifiedManifestData,
  } = useMutation({
    mutationFn: ({ manifestDatePrint, printAlreadyPrinted }) =>
      simplifiedManifest(manifestDatePrint, printAlreadyPrinted),

    onSuccess: onSuccessfulPrintManifest,

    onError: onErrorPrintManifest(history),
  });

  const {
    reset,
    register,
    handleSubmit,
    formState: { errors },
  } = useReactForm({
    defaultValues: {
      manifestDatePrint: dateFormat(new Date(), "yyyy-MM-dd"),
      printAlreadyPrinted: false,
      printManifestType: "manifest",
    },
  });

  const [show, setShow] = useState(false);

  const handleClose = () => {
    setShow(false);
    reset();
  };

  /**
   * @param {submitManifestData} data
   */
  const onSubmit = (data) => {
    if (data.printManifestType === "manifest") {
      mutatePrintManifest({
        manifestDatePrint: data.manifestDatePrint,
        printAlreadyPrinted: data.printAlreadyPrinted,
      });
    }

    if (data.printManifestType === "simplifiedManifest") {
      mutateSimplifiedManifest({
        manifestDatePrint: data.manifestDatePrint,
        printAlreadyPrinted: data.printAlreadyPrinted,
      });
    }

    handleClose();
  };

  useImperativeHandle(
    ref,
    () => ({
      showPrintManifestModal: () => {
        setShow(true);
      },
    }),
    [],
  );

  return (
    <>
      <LoadingModal
        active={isLoadingPrintManifestData || isLoadingSimplifiedManifestData}
      />
      <DialogModal
        show={show}
        title="Print Manifest"
        onHide={handleClose}
        cancelButtonProps={{ cancelText: "Close", onClick: handleClose }}
        saveButtonProps={{ className: "tw-hidden", onClick: undefined }}
      >
        <form
          className="tw-text-center tw-space-y-4 tw-mb-3 tw-mt-2"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div>
            <input
              type="date"
              className={cn(
                "form-control",
                errors.manifestDatePrint
                  ? "tw-border-red-500 hover:tw-border-red-500 focus-within:tw-border-red-500"
                  : "",
              )}
              {...register("manifestDatePrint", { required: true })}
              max={dateFormat(new Date(), "yyyy-MM-dd")}
            />

            {errors.manifestDatePrint && (
              <small className="tw-text-red-500">
                Please enter a valid date.
              </small>
            )}
          </div>

          <div>
            <select
              className={cn(
                "form-control",
                errors.printManifestType
                  ? "tw-border-red-500 hover:tw-border-red-500 focus-within:tw-border-red-500"
                  : "",
              )}
              {...register("printManifestType", { required: true })}
            >
              <option disabled value="">
                Select Manifest Type
              </option>

              {printManifestOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {errors.printManifestType && (
              <small className="tw-text-red-500">
                Please select a manifest type.
              </small>
            )}
          </div>

          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              {...register("printAlreadyPrinted")}
            />

            <label className="form-check-label">
              Include already printed orders?
            </label>

            <OverlayTrigger
              key="top"
              placement="top"
              flip
              overlay={
                <TooltipStandard id={`tooltip-top`}>
                  By default, the manifest will only include orders that have
                  not already been printed on a previous manifest. If you would
                  like the manifest to include all orders regardless of whether
                  they have been on a previous manifest or not, please check
                  this box.
                </TooltipStandard>
              }
            >
              <div className="tw-inline">
                <BsInfoCircle className="info-circle-color" />
              </div>
            </OverlayTrigger>
          </div>

          <button type="submit" className="btn btn-primary">
            Print
          </button>
        </form>
      </DialogModal>
    </>
  );
});

PrintManifestModalAdv.displayName = "PrintManifestModalAdv";

export default PrintManifestModalAdv;
